/**
 * SHER-246 — Group a submission's SurveyRecords for PDF rendering, in a way
 * that survives template modifications.
 *
 * The previous PDF code path iterated `currentSurvey.sections` and tried to
 * match each record to a live section by its field id; any record whose
 * field had been deactivated (soft-deleted in the dashboard EDC, the SHER-208
 * "orphan" case) was silently dropped. For a historical submission that
 * predates a template rename / field-replace, that meant the whole record
 * set could vanish from the PDF — the medico-legally critical artifact
 * rendered blank with only "-".
 *
 * This helper inverts the relationship: every record in the submission ends
 * up in exactly one bucket — either under its live section (looked up by
 * field id), or in `orphans` if the record's field is no longer in any
 * live section. The read view already does the equivalent for
 * show_records_section.jsx; this brings the PDF path onto the same contract.
 */

export interface SurveyFieldLike {
    id: number | string;
    name?: string;
    type?: string;
    order?: number;
    label?: string;
    options?: unknown;
}

export interface SurveySectionLike {
    uuid: string;
    name: string;
    order?: number;
    fields: SurveyFieldLike[];
}

export interface SurveyRecordLike {
    id: number | string;
    value: unknown;
    surveyField?: SurveyFieldLike;
    surveySection?: { uuid?: string } | unknown;
}

export interface SectionGroup<S extends SurveySectionLike, R extends SurveyRecordLike> {
    section: S;
    records: R[];
}

export interface GroupedRecordsForPDF<S extends SurveySectionLike, R extends SurveyRecordLike> {
    sections: SectionGroup<S, R>[];
    /**
     * Records whose surveyField is no longer in any live section (or is
     * missing entirely). The renderer must still surface these — that's
     * the medico-legal invariant of SHER-246.
     */
    orphans: R[];
}

export function groupRecordsForPDF<
    S extends SurveySectionLike,
    R extends SurveyRecordLike,
>(records: R[], sections: S[]): GroupedRecordsForPDF<S, R> {
    // Build a fast field-id -> section uuid index of the LIVE template.
    const fieldIdToSectionUuid = new Map<string, string>();
    for (const section of sections) {
        for (const field of section.fields || []) {
            fieldIdToSectionUuid.set(String(field.id), section.uuid);
        }
    }

    const sectionGroups = new Map<string, SectionGroup<S, R>>();
    for (const section of sections) {
        sectionGroups.set(section.uuid, { section, records: [] });
    }

    const orphans: R[] = [];

    for (const record of records || []) {
        const fieldId = record.surveyField?.id;
        const sectionUuid =
            fieldId !== undefined ? fieldIdToSectionUuid.get(String(fieldId)) : undefined;

        if (sectionUuid && sectionGroups.has(sectionUuid)) {
            sectionGroups.get(sectionUuid)!.records.push(record);
        } else {
            orphans.push(record);
        }
    }

    // Sort records within each section by their field's order to match how
    // the read view renders them; fall back to record id for stability.
    for (const group of sectionGroups.values()) {
        group.records.sort((a, b) => {
            const ao = a.surveyField?.order ?? Number.MAX_SAFE_INTEGER;
            const bo = b.surveyField?.order ?? Number.MAX_SAFE_INTEGER;
            if (ao !== bo) return ao - bo;
            return Number(a.id) - Number(b.id);
        });
    }

    return {
        sections: [...sectionGroups.values()],
        orphans,
    };
}
